<i18n locale="en">
{
  "export-applications": "Export Applications",
  "export-error": "Could not export applications",
  "new-job": "New Role",
  "views": {
    "applications": "Applications",
    "jobs": "Open Roles"
  }
}
</i18n>
<template>
  <UpsellTakeover v-if="showBoostTakeover" />
  <CareersGatePlaceholder v-else-if="isCareersGated" />
  <DefaultPageLayout v-else>
    <template #actions>
      <OwnButton
        :processing="isExporting"
        :text="t('export-applications')"
        @click="onExport"
      />
      <OwnButton
        :text="t('new-job')"
        variant="primary"
        @click="$router.push({ name: 'location.careers.jobs.new' })"
      />
    </template>
    <template #filter-bar>
      <OwnSegmentedControl
        :options="views"
        el="router-link"
        variant="navigation"
      />
    </template>

    <router-view />
  </DefaultPageLayout>
</template>
<script>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import DefaultPageLayout from '@/components/layouts/components/page/DefaultPageLayout.vue'
import { useApplicationScope } from '@/core/store'
import { isBrandBoostOnly } from '@/features/billing/queries/useActiveBrandPlans'
import CareersGatePlaceholder from '@/features/careers/components/CareersGatePlaceholder.vue'
import { MIB_GATES_FLAG } from '@/features/careers/featureFlags'
import { isJobBoardGated } from '@/features/careers/gating'
import UpsellTakeover from '@/features/upsell/public/UpsellTakeover.vue'
import { logError } from '@/logger'
import notify from '@/mixins/notify'
import { OlympusClient } from '@/OlympusClient'
import { OwnButton } from '@/ui/OwnButton'
import { OwnSegmentedControl } from '@/ui/OwnSegmentedControl'
import { download } from '@/utils/download'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

export default {
  name: 'CareersView',
  components: {
    CareersGatePlaceholder,
    DefaultPageLayout,
    OwnButton,
    OwnSegmentedControl,
    UpsellTakeover,
  },
  mixins: [notify],
  setup() {
    const { t } = useI18n()
    const { activeLocationId } = useApplicationScope()
    const { enabled: mibGatesEnabled } = useReactiveFeatureFlag(MIB_GATES_FLAG)

    const isCareersGated = computed(() =>
      isJobBoardGated(mibGatesEnabled.value)
    )

    // Boost-only brands get the full upsell treatment; any other gated brand
    // (including "plan not loaded yet") falls back to the plain placeholder.
    const showBoostTakeover = computed(
      () => isCareersGated.value && isBrandBoostOnly()
    )

    return { activeLocationId, isCareersGated, showBoostTakeover, t }
  },
  data() {
    return {
      isExporting: false,
      views: ['jobs', 'applications'].map((name) => ({
        label: this.t(`views.${name}`),
        value: { name: `location.careers.${name}` },
      })),
    }
  },
  methods: {
    async onExport() {
      const { activeLocationId } = this

      try {
        this.isExporting = true
        const { downloadUrl } =
          await OlympusClient.careers.v1.locations.location.exportApplications({
            params: { locationId: activeLocationId },
          })

        download(downloadUrl)
        this.$analytics.track('CSV Export', { type: 'Job Applications' })
      } catch (error) {
        logError(error)
        this.$notify(this.t('export-error'), 'error')
      } finally {
        this.isExporting = false
      }
    },
  },
}
</script>
